import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useLevel } from '../context/LevelContext';
import { ticketAPI } from '../api/client';
import { buildLevelPath, replaceUrlLanguageInPath } from '../utils/routeLanguage';
import { Menu, X, LogOut, Settings, Bell, ChevronDown } from 'lucide-react';

export const Navigation = () => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { isDark } = useTheme();
  const { selectedLevel } = useLevel();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [newTicketCount, setNewTicketCount] = useState(0);
  const userMenuRef = useRef(null);
  const levelPath = buildLevelPath(language, selectedLevel);
  const isStaff = user?.role === 'admin' || user?.role === 'teacher';
  const canUseTelegramLink = !!user && !isStaff;
  
  const isInAdmin = location.pathname.includes('/admin');

  const handleLanguageToggle = () => {
    const nextLanguage = language === 'ru' ? 'uk' : 'ru';
    const nextPath = replaceUrlLanguageInPath(location.pathname, nextLanguage);
    toggleLanguage();

    if (nextPath !== location.pathname) {
      navigate(nextPath);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchTicketSummary = async () => {
    if (user?.role !== 'admin') {
      return;
    }

    try {
      const response = await ticketAPI.getTicketSummary();
      setNewTicketCount(response.data?.newCount || 0);
    } catch (error) {
      console.error('Error fetching ticket summary:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      return undefined;
    }

    fetchTicketSummary();
    const intervalId = setInterval(fetchTicketSummary, 30000);

    return () => clearInterval(intervalId);
  }, [user?.role]);

  useEffect(() => {
    if (menuOpen && user?.role === 'admin') {
      fetchTicketSummary();
    }
  }, [menuOpen, user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  if (!user) return null;

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="navbar">
        <div className="navbar-container">
          
          {/* Logo */}
          <div 
            className="navbar-logo"
            onClick={() => navigate(levelPath)}
          >
            <span className="navbar-logo-text">Lingua<span className="navbar-logo-accent">DE</span></span>
          </div>

          {/* Right Section */}
          <div className="navbar-actions">

            {/* Language Toggle */}
            <button
              onClick={handleLanguageToggle}
              className="navbar-button hidden sm:flex"
            >
              {language === 'ru' ? 'RU' : 'UA'}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden navbar-button"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Desktop User Menu */}
            <div className="navbar-user-menu hidden md:block" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="navbar-user-button"
              >
                <span className="text-lg">{user?.role === 'admin' ? '👑' : user?.role === 'teacher' ? '👨‍🏫' : '👤'}</span>
                <span className="hidden lg:inline text-sm font-600 text-white truncate max-w-[120px]">{user?.name}</span>
                {newTicketCount > 0 && (
                  <span className="w-2 h-2 bg-coral rounded-full"></span>
                )}
                <ChevronDown size={16} className="text-muted" />
              </button>

              {/* Desktop Dropdown Menu */}
                {userMenuOpen && (
                <div className="navbar-user-menu-dropdown">
                  {/* User Info */}
                  <div className="navbar-user-info">
                    <div className="navbar-user-name">{user?.name}</div>
                    <div className="navbar-user-email">{user?.email}</div>
                    <div className="navbar-user-role">
                      {user?.role === 'admin' ? t('admin') : user?.role === 'teacher' ? t('teacher') : t('student')}
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="navbar-menu-items">
                    {user?.role === 'admin' && !isInAdmin && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item"
                      >
                        <Settings size={16} />
                        {t('adminPanel')}
                      </button>
                    )}

                    {user?.role === 'admin' && isInAdmin && (
                      <button
                        onClick={() => {
                          navigate(levelPath);
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item"
                      >
                        <Settings size={16} />
                        {t('exitAdminPanel')}
                      </button>
                    )}

                    {user?.role === 'teacher' && !isInAdmin && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item"
                      >
                        <Settings size={16} />
                        {t('teacherPanel')}
                      </button>
                    )}

                    {user?.role === 'teacher' && isInAdmin && (
                      <button
                        onClick={() => {
                          navigate(levelPath);
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item"
                      >
                        <Settings size={16} />
                        {t('exitTeacherPanel')}
                      </button>
                    )}

                    {user?.role === 'admin' && (
                      <button
                        onClick={() => {
                          navigate('/admin/tickets');
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item relative"
                      >
                        <Bell size={16} />
                        {t('tickets')}
                        {newTicketCount > 0 && (
                          <span className="ml-auto navbar-notification-badge">
                            {newTicketCount > 9 ? '9+' : newTicketCount}
                          </span>
                        )}
                      </button>
                    )}

                    {canUseTelegramLink && (
                      <button
                        onClick={() => {
                          navigate('/telegram-link');
                          setUserMenuOpen(false);
                        }}
                        className="navbar-menu-item"
                      >
                        🔗 Telegram код
                      </button>
                    )}

                    <div className="navbar-menu-divider"></div>

                    <button
                      onClick={() => {
                        handleLogout();
                        setUserMenuOpen(false);
                      }}
                      className="navbar-menu-item danger"
                    >
                      <LogOut size={16} />
                      {t('logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="navbar-mobile-menu">
          <div className="navbar-mobile-menu-content">
            
            {/* User Info */}
            <div className="pb-4 border-b border-border">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{user?.role === 'admin' ? '👑' : user?.role === 'teacher' ? '👨‍🏫' : '👤'}</span>
                <div>
                  <div className="font-bold text-white">{user?.name}</div>
                  <div className="text-xs text-muted">{user?.email}</div>
                  <div className="text-xs text-gold font-600 mt-1">
                    {user?.role === 'admin' ? t('admin') : user?.role === 'teacher' ? t('teacher') : t('student')}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="space-y-3">
              {user?.role === 'admin' && !isInAdmin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full"
                >
                  <Settings size={18} />
                  {t('adminPanel')}
                </button>
              )}

              {user?.role === 'admin' && isInAdmin && (
                <button
                  onClick={() => {
                    navigate(levelPath);
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full"
                >
                  <Settings size={18} />
                  {t('exitAdminPanel')}
                </button>
              )}

              {user?.role === 'teacher' && !isInAdmin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full"
                >
                  <Settings size={18} />
                  {t('teacherPanel')}
                </button>
              )}

              {user?.role === 'teacher' && isInAdmin && (
                <button
                  onClick={() => {
                    navigate(levelPath);
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full"
                >
                  <Settings size={18} />
                  {t('exitTeacherPanel')}
                </button>
              )}

              {user?.role === 'admin' && (
                <button
                  onClick={() => {
                    navigate('/admin/tickets');
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full relative"
                >
                  <Bell size={18} />
                  {t('tickets')}
                  {newTicketCount > 0 && (
                    <span className="ml-auto navbar-notification-badge">
                      {newTicketCount > 9 ? '9+' : newTicketCount}
                    </span>
                  )}
                </button>
              )}

              {canUseTelegramLink && (
                <button
                  onClick={() => {
                    navigate('/telegram-link');
                    setMenuOpen(false);
                  }}
                  className="navbar-menu-item w-full"
                >
                  🔗 Telegram код
                </button>
              )}

              {/* Language Toggle Mobile */}
              <button
                onClick={() => {
                  handleLanguageToggle();
                  setMenuOpen(false);
                }}
                className="navbar-menu-item w-full"
              >
                <span>{language === 'ru' ? 'RU' : 'UA'}</span>
                {language === 'ru' ? 'RU' : 'UA'}
              </button>
            </div>

            {/* Logout */}
            <button
              onClick={() => {
                handleLogout();
                setMenuOpen(false);
              }}
              className="navbar-menu-item w-full danger mt-auto"
            >
              <LogOut size={18} />
              {t('logout')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
